import { useEffect, useState } from 'react'
import { Icon } from '@iconify/react'
import Button from '../common/Button'
import Select from '../common/Select'
import { scanSessionsService } from '../../services/api/scanSessionsService'
import { toast } from 'react-toastify'

const getPageId = (page) => page?.id?.toString() || page?._id?.toString() || page?.url || ''
const getPageTitle = (page) => page?.title || page?.name || 'Home Page'
const getPageUrl = (page) => page?.url || page?.link || ''
const getSiteId = (site) => site?._id || site?.id || site?.siteUrl || ''
const getSiteName = (site) => site?.siteName || site?.name || site?.siteUrl || 'Unnamed Site'
const getSiteUrl = (site) => site?.siteUrl || site?.url || site?.domain || ''

const getTodayDateString = () => {
  const now = new Date()
  const year = now.getFullYear()
  const month = String(now.getMonth() + 1).padStart(2, '0')
  const day = String(now.getDate()).padStart(2, '0')
  return `${year}-${month}-${day}`
}

const formatPageCount = (count) => {
  if (count === 0) return 'No pages selected'
  if (count === 1) return '1 page selected'
  return `${count} pages selected`
}

const ScheduleSwitch = ({ checked, onChange }) => (
  <button
    type="button"
    onClick={() => onChange(!checked)}
    aria-pressed={checked}
    className={`inline-flex h-7 w-12 items-center rounded-full border p-0.5 transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-primary/25 ${
      checked
        ? 'border-primary bg-primary shadow-sm'
        : 'border-gray-300 bg-gray-200 hover:bg-gray-300'
    }`}
  >
    <span
      className={`flex h-6 w-6 items-center justify-center rounded-full bg-white text-[10px] shadow-sm transition-transform duration-200 ${
        checked ? 'translate-x-5 text-primary' : 'translate-x-0 text-gray-400'
      }`}
    >
      <Icon icon={checked ? 'mdi:check' : 'mdi:minus'} />
    </span>
  </button>
)

const ScanPlanner = ({
  platformName,
  connections = [],
  selectedConnection,
  onSelectConnection = () => {},
  pages,
  selectedPage,
  onSelectPage,
  isGettingPages,
  pagesError,
}) => {
  const [sessionName, setSessionName] = useState('')
  const [activeSiteId, setActiveSiteId] = useState('')
  const [selectedSiteIds, setSelectedSiteIds] = useState([])
  const [selectedPageIdsBySite, setSelectedPageIdsBySite] = useState({})
  const [pagesBySite, setPagesBySite] = useState({}) // Store pages for each site
  const [scheduleEnabled, setScheduleEnabled] = useState(false)
  const [frequency, setFrequency] = useState('weekly')
  const [scanTime, setScanTime] = useState('09:00')
  const [scanDate, setScanDate] = useState('')
  const [notifyByEmail, setNotifyByEmail] = useState(true)
  const [isCreating, setIsCreating] = useState(false)

  const currentSiteId = getSiteId(selectedConnection)
  const activeSite = connections.find((site) => getSiteId(site) === activeSiteId) || selectedConnection
  const activePageIds = selectedPageIdsBySite[activeSiteId] || []
  const selectedSites = connections.filter((site) => selectedSiteIds.includes(getSiteId(site)))
  const totalSelectedPages = Object.values(selectedPageIdsBySite).reduce(
    (total, pageIds) => total + pageIds.length,
    0,
  )
  const emptyState = isGettingPages || pagesError || pages.length === 0 || !activeSiteId

  useEffect(() => {
    if (!currentSiteId) return
    setActiveSiteId((prev) => prev || currentSiteId)
    setSelectedSiteIds((prev) => (prev.includes(currentSiteId) ? prev : [...prev, currentSiteId]))
  }, [currentSiteId])

  // Store pages when they are fetched for the active site
  useEffect(() => {
    if (activeSiteId && pages.length > 0) {
      setPagesBySite((prev) => ({
        ...prev,
        [activeSiteId]: pages,
      }))
    }
  }, [activeSiteId, pages])

  useEffect(() => {
    const selectedId = getPageId(selectedPage)
    if (!activeSiteId || !selectedId) return
    setSelectedPageIdsBySite((prev) => {
      const existing = prev[activeSiteId] || []
      if (existing.includes(selectedId)) return prev
      return {
        ...prev,
        [activeSiteId]: [...existing, selectedId],
      }
    })
  }, [selectedPage, activeSiteId])

  const openSite = (site) => {
    const siteId = getSiteId(site)
    if (!siteId) return
    setActiveSiteId(siteId)
    onSelectConnection(site)
    setSelectedSiteIds((prev) => (prev.includes(siteId) ? prev : [...prev, siteId]))
  }

  const toggleSite = (site) => {
    const siteId = getSiteId(site)
    if (!siteId) return

    setSelectedSiteIds((prev) => {
      const next = prev.includes(siteId)
        ? prev.filter((id) => id !== siteId)
        : [...prev, siteId]

      if (!next.includes(activeSiteId)) {
        const nextActiveSite = connections.find((item) => getSiteId(item) === next[0])
        setActiveSiteId(next[0] || '')
        if (nextActiveSite) onSelectConnection(nextActiveSite)
      }

      return next
    })
  }

  const togglePage = (page) => {
    const pageId = getPageId(page)
    if (!activeSiteId || !pageId) return

    setSelectedSiteIds((prev) => (prev.includes(activeSiteId) ? prev : [...prev, activeSiteId]))
    setSelectedPageIdsBySite((prev) => {
      const existing = prev[activeSiteId] || []
      const next = existing.includes(pageId)
        ? existing.filter((id) => id !== pageId)
        : [...existing, pageId]
      const firstSelected = pages.find((item) => next.includes(getPageId(item)))
      onSelectPage(firstSelected || null)
      return {
        ...prev,
        [activeSiteId]: next,
      }
    })
  }

  const removeSelectedPage = (siteId, pageId) => {
    setSelectedPageIdsBySite((prev) => ({
      ...prev,
      [siteId]: (prev[siteId] || []).filter((id) => id !== pageId),
    }))
  }

  const selectAllSites = () => {
    const allSiteIds = connections.map(getSiteId).filter(Boolean)
    setSelectedSiteIds(allSiteIds)
    if (!activeSiteId && connections[0]) {
      openSite(connections[0])
    }
  }

  const clearSites = () => {
    setSelectedSiteIds([])
    setSelectedPageIdsBySite({})
    setPagesBySite({})
    setActiveSiteId('')
    onSelectPage(null)
  }

  const selectAllPagesForActiveSite = () => {
    if (!activeSiteId) return
    const allPageIds = pages.map(getPageId).filter(Boolean)
    setSelectedSiteIds((prev) => (prev.includes(activeSiteId) ? prev : [...prev, activeSiteId]))
    setSelectedPageIdsBySite((prev) => ({
      ...prev,
      [activeSiteId]: allPageIds,
    }))
    if (pages[0]) onSelectPage(pages[0])
  }

  const clearActiveSitePages = () => {
    if (!activeSiteId) return
    setSelectedPageIdsBySite((prev) => ({
      ...prev,
      [activeSiteId]: [],
    }))
    onSelectPage(null)
  }

  const clearSession = () => {
    setSelectedPageIdsBySite({})
    setSelectedSiteIds(currentSiteId ? [currentSiteId] : [])
    setPagesBySite(currentSiteId && pages.length > 0 ? { [currentSiteId]: pages } : {})
    onSelectPage(null)
  }

  const getSiteSelectedCount = (siteId) => (selectedPageIdsBySite[siteId] || []).length

  const handleCreateSession = async (scanNow = false) => {
    if (!sessionName.trim()) {
      toast.error('Please enter a session name')
      return
    }

    if (selectedSiteIds.length === 0) {
      toast.error('Please select at least one site')
      return
    }

    if (totalSelectedPages === 0) {
      toast.error('Please select at least one page')
      return
    }

    if (scheduleEnabled && !scanNow) {
      if (!scanTime) {
        toast.error('Please select a scan time')
        return
      }
      if ((frequency === 'weekly' || frequency === 'monthly') && !scanDate) {
        toast.error('Please select a start date')
        return
      }
    }

    setIsCreating(true)
    try {
      const getRelativeUrl = (fullUrl, siteUrl) => {
        if (!fullUrl) return '/'
        if (fullUrl.startsWith('/')) return fullUrl
        try {
          const url = new URL(fullUrl)
          return url.pathname + url.search + url.hash || '/'
        } catch {
          return fullUrl
        }
      }

      const sites = selectedSiteIds
        .map((siteId) => {
          const site = connections.find((c) => getSiteId(c) === siteId)
          if (!site) return null

          const pageIds = selectedPageIdsBySite[siteId] || []
          if (pageIds.length === 0) return null

          const sitePlatform = (site.platform || 'wordpress').toLowerCase()
          const siteUrl = getSiteUrl(site)
          const sitePagesData = pagesBySite[siteId] || [] // Get cached pages for this site

          const pagesMapped = pageIds
            .map((pageId) => {
              const page = sitePagesData.find((p) => getPageId(p) === pageId)
              if (!page) return null

              const relativeUrl = getRelativeUrl(getPageUrl(page), siteUrl)
              const rawId = page?.id || page?._id || pageId
              const numericId = parseInt(rawId, 10)
              
              const pageData = {
                pageId: pageId.toString(),
                title: getPageTitle(page),
                url: relativeUrl,
              }

              if (sitePlatform === 'wordpress' && !isNaN(numericId)) {
                pageData.wpPageId = numericId
                pageData.pageBuilder = page.pageBuilder || 'elementor'
              }

              return pageData
            })
            .filter(Boolean)

          if (pagesMapped.length === 0) return null

          return {
            connectionId: site._id,
            pages: pagesMapped,
          }
        })
        .filter(Boolean)

      if (sites.length === 0) {
        toast.error('No valid sites or pages found')
        return
      }

      const payload = {
        name: sessionName,
        sites,
        notifyByEmail,
        scanNow,
      }

      if (scheduleEnabled && !scanNow) {
        payload.schedule = {
          frequency,
          time: scanTime,
        }
        if ((frequency === 'weekly' || frequency === 'monthly') && scanDate) {
          payload.schedule.startDate = scanDate
        }
      }

      console.log('Sending payload:', JSON.stringify(payload, null, 2))

      const response = await scanSessionsService.createSession(payload)
      
      if (response.success) {
        toast.success(
          scanNow 
            ? 'Scan session created and started successfully!' 
            : 'Scan session created and scheduled successfully!'
        )
        
        setSessionName('')
        setSelectedPageIdsBySite({})
        setSelectedSiteIds(currentSiteId ? [currentSiteId] : [])
        setScheduleEnabled(false)
        setFrequency('weekly')
        setScanTime('09:00')
        setScanDate('')
        setNotifyByEmail(true)
        onSelectPage(null)
      }
    } catch (error) {
      const errorMessage = error.response?.data?.message || 'Failed to create scan session'
      toast.error(errorMessage)
      console.error('Error creating scan session:', error)
    } finally {
      setIsCreating(false)
    }
  }

  return (
    <section className="mt-6 rounded-xl border border-border-light bg-white">
      <div className="flex flex-col gap-4 border-b border-gray-100 px-4 py-4 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex items-center gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-sky-50 text-primary">
            <Icon icon="mdi:calendar-clock" className="text-xl" />
          </span>
          <div>
            <h3 className="text-sm font-bold text-text-dark">Scan Session Builder</h3>
            <p className="text-xs text-text-secondary">
              {selectedSiteIds.length} sites, {formatPageCount(totalSelectedPages)}
            </p>
          </div>
        </div>

        {totalSelectedPages > 0 && (
          <button
            type="button"
            onClick={clearSession}
            className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-3 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100"
          >
            <Icon icon="mdi:close-circle-outline" className="text-sm" />
            Clear session
          </button>
        )}
      </div>

      <div className="grid gap-4 p-4 xl:grid-cols-[minmax(0,1.35fr)_minmax(330px,0.65fr)]">
        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 p-3">
            <label className="block">
              <span className="mb-1 block text-xs font-semibold text-gray-600">Session Name</span>
              <input
                type="text"
                value={sessionName}
                onChange={(event) => setSessionName(event.target.value)}
                placeholder="Example: Weekly client accessibility scan"
                className="form-control h-10"
              />
            </label>
          </div>

          <div className="rounded-lg border border-gray-200">
            <div className="flex flex-col gap-3 border-b border-gray-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold text-text-dark">Select Sites</p>
                <p className="text-xs text-text-secondary">
                  Choose one or more sites. Page selections stay saved when you switch sites.
                </p>
              </div>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={selectAllSites}
                  disabled={connections.length === 0}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icon icon="mdi:select-all" />
                  All Sites
                </button>
                <button
                  type="button"
                  onClick={clearSites}
                  disabled={selectedSiteIds.length === 0}
                  className="inline-flex h-9 items-center justify-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-3 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                >
                  <Icon icon="mdi:close-circle-outline" />
                  Clear
                </button>
              </div>
            </div>

            <div className="grid gap-2 p-3 md:grid-cols-2">
              {connections.map((site) => {
                const siteId = getSiteId(site)
                const isSelected = selectedSiteIds.includes(siteId)
                const isActive = activeSiteId === siteId
                return (
                  <div
                    key={siteId}
                    className={`rounded-lg border p-3 transition-colors ${
                      isActive
                        ? 'border-primary bg-sky-50'
                        : isSelected
                          ? 'border-sky-200 bg-white'
                          : 'border-gray-200 bg-white'
                    }`}
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => toggleSite(site)}
                        className="mt-1 h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                      />
                      <button
                        type="button"
                        onClick={() => openSite(site)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <p className="truncate text-sm font-semibold text-text-dark">{getSiteName(site)}</p>
                        <p className="truncate text-xs text-text-secondary">{getSiteUrl(site)}</p>
                        <p className="mt-1 text-xs font-semibold text-primary">
                          {formatPageCount(getSiteSelectedCount(siteId))}
                        </p>
                      </button>
                    </div>
                  </div>
                )
              })}
            </div>
          </div>

          <div className="rounded-lg border border-gray-200">
            <div className="flex flex-col gap-2 border-b border-gray-100 p-3 sm:flex-row sm:items-center sm:justify-between">
              <div>
                <p className="text-sm font-bold text-text-dark">
                  {activeSite ? getSiteName(activeSite) : platformName} Pages
                </p>
                <p className="text-xs text-text-secondary">
                  {pages.length} available pages for the active site
                </p>
              </div>
              {activeSiteId && (
                <div className="flex flex-wrap items-center gap-2">
                  <span className="inline-flex w-fit items-center gap-1 rounded-full bg-primary/10 px-3 py-1 text-xs font-semibold text-primary">
                    <Icon icon="mdi:checkbox-marked-circle-outline" />
                    {formatPageCount(activePageIds.length)}
                  </span>
                  <button
                    type="button"
                    onClick={selectAllPagesForActiveSite}
                    disabled={emptyState}
                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-gray-200 px-3 text-xs font-semibold text-gray-600 transition-colors hover:border-primary hover:text-primary disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon icon="mdi:checkbox-multiple-marked-outline" />
                    All Pages
                  </button>
                  <button
                    type="button"
                    onClick={clearActiveSitePages}
                    disabled={activePageIds.length === 0}
                    className="inline-flex h-8 items-center justify-center gap-1.5 rounded-lg border border-red-100 bg-red-50 px-3 text-xs font-semibold text-red-600 transition-colors hover:bg-red-100 disabled:cursor-not-allowed disabled:opacity-40"
                  >
                    <Icon icon="mdi:close" />
                    Clear
                  </button>
                </div>
              )}
            </div>

            {activePageIds.length > 0 && (
              <div className="border-b border-gray-100 bg-sky-50/60 px-3 py-2">
                <div className="flex items-center gap-2 overflow-x-auto">
                  <span className="shrink-0 text-xs font-semibold text-primary">Selected:</span>
                  {pages
                    .filter((page) => activePageIds.includes(getPageId(page)))
                    .slice(0, 5)
                    .map((page) => (
                      <span
                        key={getPageId(page)}
                        className="inline-flex h-7 max-w-48 shrink-0 items-center gap-1.5 rounded-full border border-sky-100 bg-white px-2 text-xs font-medium text-gray-700"
                        title={getPageTitle(page)}
                      >
                        <span className="truncate">{getPageTitle(page)}</span>
                        <button
                          type="button"
                          onClick={() => removeSelectedPage(activeSiteId, getPageId(page))}
                          className="rounded-full p-0.5 text-gray-400 transition-colors hover:bg-red-50 hover:text-red-600"
                          title="Remove page"
                        >
                          <Icon icon="mdi:close" className="text-sm" />
                        </button>
                      </span>
                    ))}
                  {activePageIds.length > 5 && (
                    <span className="shrink-0 rounded-full bg-primary/10 px-2 py-1 text-xs font-semibold text-primary">
                      +{activePageIds.length - 5} more
                    </span>
                  )}
                </div>
              </div>
            )}

            {isGettingPages && (
              <div className="flex h-64 items-center justify-center text-sm text-gray-500">
                <Icon icon="mdi:loading" className="mr-2 animate-spin text-primary" />
                Loading pages
              </div>
            )}

            {!isGettingPages && pagesError && (
              <div className="m-3 rounded-lg border border-red-200 bg-red-50 p-4 text-sm text-red-600">
                {pagesError}
              </div>
            )}

            {!isGettingPages && !pagesError && pages.length === 0 && (
              <div className="flex h-64 flex-col items-center justify-center text-center">
                <Icon icon="mdi:file-search-outline" className="mb-2 text-3xl text-gray-300" />
                <p className="text-sm font-medium text-gray-700">No pages found</p>
              </div>
            )}

            {!isGettingPages && !pagesError && pages.length > 0 && (
              <div className={`${activePageIds.length > 0 ? 'h-64' : 'h-72'} space-y-2 overflow-y-auto p-3`}>
                {pages.map((page) => {
                  const pageId = getPageId(page)
                  const isChecked = activePageIds.includes(pageId)
                  return (
                    <label
                      key={pageId}
                      className={`flex cursor-pointer items-center gap-3 rounded-lg border px-3 py-2.5 transition-colors ${
                        isChecked
                          ? 'border-primary bg-sky-50'
                          : 'border-gray-200 bg-white hover:border-primary/50'
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => togglePage(page)}
                        disabled={!activeSiteId}
                        className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary disabled:cursor-not-allowed disabled:opacity-40"
                      />
                      <span className="min-w-0 flex-1">
                        <span className="block truncate text-sm font-semibold text-gray-800">
                          {getPageTitle(page)}
                        </span>
                        {getPageUrl(page) && (
                          <span className="block truncate text-xs text-gray-400">
                            {getPageUrl(page)}
                          </span>
                        )}
                      </span>
                    </label>
                  )
                })}
              </div>
            )}
          </div>
        </div>

        <div className="space-y-4">
          <div className="rounded-lg border border-gray-200 bg-white">
            <div className="flex items-center justify-between gap-3 border-b border-gray-100 px-3 py-2.5">
              <div>
                <p className="text-sm font-bold text-text-dark">Schedule</p>
                <p className="text-xs text-text-secondary">{scheduleEnabled ? 'Enabled' : 'Off'}</p>
              </div>
              <div className="flex items-center gap-2 rounded-full bg-gray-50 px-2 py-1">
                <span className="text-xs font-semibold text-gray-500">Auto</span>
                <ScheduleSwitch checked={scheduleEnabled} onChange={setScheduleEnabled} />
              </div>
            </div>

            <div className="p-3">
              <div className="rounded-lg bg-gray-50 p-3">
                <div className="flex items-start gap-2">
                  <Icon icon="mdi:clock-fast" className="mt-0.5 text-lg text-primary" />
                  <div>
                    <p className="text-xs font-bold text-gray-700">
                      {scheduleEnabled ? 'Scheduled scans are on' : 'Run scans manually'}
                    </p>
                    <p className="text-xs text-gray-500">
                      {scheduleEnabled
                        ? `${selectedSiteIds.length} sites and ${formatPageCount(totalSelectedPages)} will be scanned ${frequency} at ${scanTime}.`
                        : 'Turn on Auto to save a recurring scan schedule.'}
                    </p>
                  </div>
                </div>
              </div>

              <div className="mt-3 grid grid-cols-1 gap-3 sm:grid-cols-2">
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Frequency</span>
                  <Select
                    value={frequency}
                    onChange={(event) => setFrequency(event.target.value)}
                    disabled={!scheduleEnabled}
                    className="h-10"
                    wrapperClassName=""
                  >
                    <option value="daily">Daily</option>
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </Select>
                </label>
                <label className="block">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Time</span>
                  <input
                    type="time"
                    value={scanTime}
                    onChange={(event) => setScanTime(event.target.value)}
                    disabled={!scheduleEnabled}
                    className="form-control h-10"
                  />
                </label>
                <label className="block sm:col-span-2">
                  <span className="mb-1 block text-xs font-semibold text-gray-600">Start Date</span>
                  <input
                    type="date"
                    value={scanDate}
                    onChange={(event) => setScanDate(event.target.value)}
                    disabled={!scheduleEnabled}
                    min={getTodayDateString()}
                    className="form-control h-10"
                  />
                </label>
              </div>

              <label className="mt-3 flex items-center gap-2 text-sm text-gray-600">
                <input
                  type="checkbox"
                  checked={notifyByEmail}
                  onChange={(event) => setNotifyByEmail(event.target.checked)}
                  disabled={!scheduleEnabled}
                  className="h-4 w-4 rounded border-gray-300 text-primary focus:ring-primary"
                />
                Email report after scan
              </label>

              <div className="mt-4 grid grid-cols-1 gap-2 sm:grid-cols-2">
                <Button
                  variant="secondary"
                  size="sm"
                  icon="mdi:play-circle-outline"
                  onClick={() => handleCreateSession(true)}
                  disabled={totalSelectedPages === 0 || isCreating}
                  className="w-full h-10"
                >
                  {isCreating ? 'Creating...' : 'Scan Now'}
                </Button>
                <Button
                  size="sm"
                  icon="mdi:calendar-check"
                  onClick={() => handleCreateSession(false)}
                  disabled={!scheduleEnabled || totalSelectedPages === 0 || isCreating}
                  className="w-full h-10"
                >
                  {isCreating ? 'Saving...' : 'Save Schedule'}
                </Button>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-gray-200 bg-white p-3">
            <p className="text-sm font-bold text-text-dark">Session Summary</p>
            <div className="mt-3 space-y-2">
              {selectedSites.length === 0 ? (
                <p className="rounded-lg border border-dashed border-gray-200 p-4 text-center text-sm text-text-secondary">
                  No sites selected yet.
                </p>
              ) : (
                selectedSites.map((site) => {
                  const siteId = getSiteId(site)
                  return (
                    <div key={siteId} className="rounded-lg bg-gray-50 p-3">
                      <p className="truncate text-sm font-semibold text-text-dark">{getSiteName(site)}</p>
                      <p className="text-xs text-text-secondary">{formatPageCount(getSiteSelectedCount(siteId))}</p>
                    </div>
                  )
                })
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  )
}

export default ScanPlanner
